import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';

// ================ Action types ================ //

export const CHANGE_PASSWORD_REQUEST = 'app/PasswordChangePage/CHANGE_PASSWORD_REQUEST';
export const CHANGE_PASSWORD_SUCCESS = 'app/PasswordChangePage/CHANGE_PASSWORD_SUCCESS';
export const CHANGE_PASSWORD_ERROR = 'app/PasswordChangePage/CHANGE_PASSWORD_ERROR';

export const CHANGE_PASSWORD_CLEAR = 'app/PasswordChangePage/CHANGE_PASSWORD_CLEAR';

export const RESET_PASSWORD_REQUEST = 'app/PasswordChangePage/RESET_PASSWORD_REQUEST';
export const RESET_PASSWORD_SUCCESS = 'app/PasswordChangePage/RESET_PASSWORD_SUCCESS';
export const RESET_PASSWORD_ERROR = 'app/PasswordChangePage/RESET_PASSWORD_ERROR';

// API Keys
export const LOAD_API_KEYS_REQUEST = 'app/PasswordChangePage/LOAD_API_KEYS_REQUEST';
export const LOAD_API_KEYS_SUCCESS = 'app/PasswordChangePage/LOAD_API_KEYS_SUCCESS';
export const LOAD_API_KEYS_ERROR = 'app/PasswordChangePage/LOAD_API_KEYS_ERROR';

export const GENERATE_API_KEY_REQUEST = 'app/PasswordChangePage/GENERATE_API_KEY_REQUEST';
export const GENERATE_API_KEY_SUCCESS = 'app/PasswordChangePage/GENERATE_API_KEY_SUCCESS';
export const GENERATE_API_KEY_ERROR = 'app/PasswordChangePage/GENERATE_API_KEY_ERROR';
export const CLEAR_GENERATED_KEY = 'app/PasswordChangePage/CLEAR_GENERATED_KEY';

export const REVOKE_API_KEY_REQUEST = 'app/PasswordChangePage/REVOKE_API_KEY_REQUEST';
export const REVOKE_API_KEY_SUCCESS = 'app/PasswordChangePage/REVOKE_API_KEY_SUCCESS';
export const REVOKE_API_KEY_ERROR = 'app/PasswordChangePage/REVOKE_API_KEY_ERROR';

// ================ Reducer ================ //

const initialState = {
  changePasswordError: null,
  changePasswordInProgress: false,
  passwordChanged: false,
  resetPasswordInProgress: false,
  resetPasswordError: null,
  // API Keys
  apiKeys: null,
  loadingKeys: false,
  loadKeysError: null,
  generatedKey: null,
  generatingKey: false,
  generateKeyError: null,
  revokingKey: false,
  revokeKeyError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CHANGE_PASSWORD_REQUEST:
      return {
        ...state,
        changePasswordInProgress: true,
        changePasswordError: null,
        passwordChanged: false,
      };
    case CHANGE_PASSWORD_SUCCESS:
      return { ...state, changePasswordInProgress: false, passwordChanged: true };
    case CHANGE_PASSWORD_ERROR:
      return { ...state, changePasswordInProgress: false, changePasswordError: payload };

    case CHANGE_PASSWORD_CLEAR:
      return { ...initialState };

    case RESET_PASSWORD_REQUEST:
      return { ...state, resetPasswordInProgress: true, resetPasswordError: null };
    case RESET_PASSWORD_SUCCESS:
      return { ...state, resetPasswordInProgress: false };
    case RESET_PASSWORD_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, resetPasswordInProgress: false, resetPasswordError: payload };

    // API Keys
    case LOAD_API_KEYS_REQUEST:
      return { ...state, loadingKeys: true, loadKeysError: null };
    case LOAD_API_KEYS_SUCCESS:
      return { ...state, loadingKeys: false, apiKeys: payload };
    case LOAD_API_KEYS_ERROR:
      return { ...state, loadingKeys: false, loadKeysError: payload };

    case GENERATE_API_KEY_REQUEST:
      return { ...state, generatingKey: true, generateKeyError: null };
    case GENERATE_API_KEY_SUCCESS:
      return {
        ...state,
        generatingKey: false,
        generatedKey: payload,
        // Add the new key to the list (without the secret)
        apiKeys: state.apiKeys
          ? [
              ...state.apiKeys,
              {
                keyId: payload.keyId,
                name: payload.name,
                environment: payload.environment,
                scopes: payload.scopes,
                createdAt: payload.createdAt,
                lastUsedAt: null,
                expiresAt: payload.expiresAt,
                revokedAt: null,
                useCount: 0,
                preview: `sk_${payload.environment}_${payload.keyId}_****`,
                active: true,
              },
            ]
          : state.apiKeys,
      };
    case GENERATE_API_KEY_ERROR:
      return { ...state, generatingKey: false, generateKeyError: payload };
    case CLEAR_GENERATED_KEY:
      return { ...state, generatedKey: null };

    case REVOKE_API_KEY_REQUEST:
      return { ...state, revokingKey: true, revokeKeyError: null };
    case REVOKE_API_KEY_SUCCESS:
      return {
        ...state,
        revokingKey: false,
        // Mark the key as inactive
        apiKeys: state.apiKeys
          ? state.apiKeys.map(key =>
              key.keyId === payload ? { ...key, active: false, revokedAt: new Date().toISOString() } : key
            )
          : state.apiKeys,
      };
    case REVOKE_API_KEY_ERROR:
      return { ...state, revokingKey: false, revokeKeyError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const changePasswordRequest = () => ({ type: CHANGE_PASSWORD_REQUEST });
export const changePasswordSuccess = () => ({ type: CHANGE_PASSWORD_SUCCESS });
export const changePasswordError = error => ({
  type: CHANGE_PASSWORD_ERROR,
  payload: error,
  error: true,
});

export const changePasswordClear = () => ({ type: CHANGE_PASSWORD_CLEAR });

export const resetPasswordRequest = () => ({ type: RESET_PASSWORD_REQUEST });

export const resetPasswordSuccess = () => ({ type: RESET_PASSWORD_SUCCESS });

export const resetPasswordError = e => ({
  type: RESET_PASSWORD_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const changePassword = params => (dispatch, getState, sdk) => {
  dispatch(changePasswordRequest());
  const { newPassword, currentPassword } = params;

  return sdk.currentUser
    .changePassword({ newPassword, currentPassword })
    .then(() => dispatch(changePasswordSuccess()))
    .catch(e => {
      dispatch(changePasswordError(storableError(storableError(e))));
      // This is thrown so that form can be cleared
      // after a timeout on changePassword submit handler
      throw e;
    });
};

export const resetPassword = email => (dispatch, getState, sdk) => {
  dispatch(resetPasswordRequest());
  return sdk.passwordReset
    .request({ email })
    .then(() => dispatch(resetPasswordSuccess()))
    .catch(e => dispatch(resetPasswordError(storableError(e))));
};

// ================ API Keys Action Creators ================ //

export const loadApiKeysRequest = () => ({ type: LOAD_API_KEYS_REQUEST });
export const loadApiKeysSuccess = apiKeys => ({
  type: LOAD_API_KEYS_SUCCESS,
  payload: apiKeys,
});
export const loadApiKeysError = error => ({
  type: LOAD_API_KEYS_ERROR,
  payload: error,
  error: true,
});

export const generateApiKeyRequest = () => ({ type: GENERATE_API_KEY_REQUEST });
export const generateApiKeySuccess = apiKey => ({
  type: GENERATE_API_KEY_SUCCESS,
  payload: apiKey,
});
export const generateApiKeyError = error => ({
  type: GENERATE_API_KEY_ERROR,
  payload: error,
  error: true,
});
export const clearGeneratedKey = () => ({ type: CLEAR_GENERATED_KEY });

export const revokeApiKeyRequest = () => ({ type: REVOKE_API_KEY_REQUEST });
export const revokeApiKeySuccess = keyId => ({
  type: REVOKE_API_KEY_SUCCESS,
  payload: keyId,
});
export const revokeApiKeyError = error => ({
  type: REVOKE_API_KEY_ERROR,
  payload: error,
  error: true,
});

// ================ API Keys Thunks ================ //

/**
 * Load API keys for the current user
 */
export const loadApiKeys = () => (dispatch, getState, sdk) => {
  dispatch(loadApiKeysRequest());

  return fetch('/api/api-key/list', {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to load API keys');
      }
      return response.json();
    })
    .then(data => {
      dispatch(loadApiKeysSuccess(data.apiKeys || []));
      return data.apiKeys;
    })
    .catch(e => {
      dispatch(loadApiKeysError(storableError(e)));
      throw e;
    });
};

/**
 * Generate a new API key
 */
export const generateApiKey = params => (dispatch, getState, sdk) => {
  dispatch(generateApiKeyRequest());

  return fetch('/api/api-key/generate', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })
    .then(response => {
      if (!response.ok) {
        return response.json().then(errorData => {
          throw new Error(errorData.message || 'Failed to generate API key');
        });
      }
      return response.json();
    })
    .then(data => {
      dispatch(generateApiKeySuccess(data));
      return data;
    })
    .catch(e => {
      dispatch(generateApiKeyError(storableError(e)));
      throw e;
    });
};

/**
 * Revoke an API key
 */
export const revokeApiKey = keyId => (dispatch, getState, sdk) => {
  dispatch(revokeApiKeyRequest());

  return fetch('/api/api-key/revoke', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ keyId }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to revoke API key');
      }
      return response.json();
    })
    .then(data => {
      dispatch(revokeApiKeySuccess(keyId));
      return data;
    })
    .catch(e => {
      dispatch(revokeApiKeyError(storableError(e)));
      throw e;
    });
};
